import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfessionalViewModel } from '../../view-model/professional/professional-view-model';
import userStore from '../../store/session-user-store';

interface Language {
  code: string;
  name: string;
}

interface LanguageSwitcherProps {
  className?: string;
  position?: 'left' | 'right';
}

const languages: Language[] = [
  { code: 'fr', name: 'Français' },
  { code: 'en', name: 'English' }
];

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className = '', position = 'right' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const professionalViewModel = useProfessionalViewModel();
  const authStore = userStore;
  
  // Trouver la langue actuelle
  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const navigate = useNavigate();
  const location = useLocation();

  const changeLanguage = (lng: string) => {
    // Extraire le préfixe de langue actuel
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}(\/|$)/, '/');
    
    // Construire le nouveau chemin avec le nouveau préfixe de langue
    const newPath = `/${lng}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
    
    // Changer la langue dans i18n
    i18n.changeLanguage(lng);
    
    if (authStore.isUserAuthenticated) {
      professionalViewModel.setLanguage(lng);
      professionalViewModel.handleUpdateLanguage();
    }
    
    // Naviguer vers la nouvelle URL avec le préfixe de langue
    navigate(newPath);
    
    setIsOpen(false);
  };
  
  // Fermer le dropdown si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative inline-block z-50 ${className}`} ref={dropdownRef}>
      <button 
        className="flex items-center bg-white border border-gray-200 rounded-full py-1.5 px-3 text-sm transition-all duration-200 text-gray-700 shadow-sm hover:shadow-md"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="mr-1.5 flex items-center text-primary">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 8l6 6"/>
          <path d="m4 14 6-6 2-3"/>
          <path d="M2 5h12"/>
          <path d="M7 2h1"/>
          <path d="m22 22-5-10-5 10"/>
          <path d="M14 18h6"/>
        </svg>
        </span>
        <span className="font-medium mr-1">{currentLanguage.code.toUpperCase()}</span>
        <span className={`flex items-center transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </span>
      </button>
      
      {isOpen && (
        <div className={`absolute top-full mt-1.5 ${position === 'right' ? 'right-0' : 'left-0'} bg-white rounded-xl shadow-lg w-40 overflow-hidden animate-fadeIn z-[51] origin-top`}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={`flex items-center w-full px-4 py-2.5 border-none bg-transparent text-left cursor-pointer transition-colors hover:bg-gray-50 ${lang.code === i18n.language ? 'bg-green-50 text-primary font-medium' : ''}`}
              onClick={() => changeLanguage(lang.code)}
            >
              <span className="text-sm">{lang.name} ({lang.code})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
